import {
  TimelineAdapter,
  TimelineAdapterId,
  TimelineEventData
} from './TimelineAdapter'
import {TimelineActionManager} from './TimelineActionManager'
import {Subject} from 'observables'

export interface CreateEventBase<E> {
  // UNIQUE IDENTIFIER FROM WHERE WAS FIRED THE EVENT
  adapterId: TimelineAdapterId
  // MESSAGE CONTENT
  message?: string
  // CUSTOM DATA
  data?: E
}

/**
 * The producer should be feed through all kind of adapters/services
 * These adapters might be: chat, assignments, file resources, etc
 */
export abstract class TimelineEventProducer {
  // IMPLEMENT ALL ADAPTERS THAT ARE REQUIRED
  protected abstract dataSourceServices: TimelineAdapter<unknown>[]
  // EVENTS LIST
  protected data: Subject<TimelineEventData<unknown>[]> = new Subject<
    TimelineEventData<unknown>[]
  >()
  // EVENT HISTORY - SESSION EVENTS ONLY
  protected history: TimelineEventData<unknown>[] = []

  public abstract actionManager: TimelineActionManager

  // FETCH DATA FROM API FROM EVERY LISTED ADAPTER
  protected async fetch() {
    const data = await Promise.all(
      this.dataSourceServices.map((service) => service.load())
    )
    const parsed = data.flat().sort((a, b) => a.date - b.date)
    this.history.push(...parsed)
    this.data.next(parsed)
    this.evaluateInitialLoading(parsed)
  }

  // YOU CAN EVALUATE ACCUMULATED EVENT LIST AND TRIGGER SOME ACTIONS
  protected abstract evaluateInitialLoading(
    acc: TimelineEventData<unknown>[]
  ): void

  // SUBSCRIBE AND FETCH DATA IMMEDIATELY
  public subscribe(observer: (values: TimelineEventData<unknown>[]) => void) {
    this.data.subscribe(observer)
    return this.fetch().then()
  }

  // FREE RESOURCE
  public complete() {
    this.dataSourceServices.forEach((adapter) => adapter.complete())
    this.data.complete()
    this.actionManager.complete()
  }

  // INSERT NEW DATA INTO THE FEED
  public update(event: TimelineEventData<unknown>[]) {
    this.history.push(...event)
    this.data.next(this.history)
  }

  public remove(eventId: string) {
    const index = this.history.findIndex((e) => e._id === eventId)
    if (index !== -1) {
      this.history.splice(index, 1)
      this.data.next(this.history)
    }
  }

  /**
   * IMPLEMENT EVENT CREATION WITH CUSTOM DATA - THIS SHOULD CALL API SERVICE AND ON SUCCESS TO UPDATE THE FEED DATA
   *
   * All createEvent method should end with `this.update([some events])`
   * @param data
   */
  public abstract createEvent(
    data: CreateEventBase<unknown>
  ): Promise<TimelineEventData<unknown> | undefined>

  // Try resend event return bool whether resend is success
  public abstract reCreateEvent(eventId: string): Promise<boolean>

  // GET CURRENT ACCUMULATED EVENT LIST
  public getHistory() {
    return this.history
  }
}
